import { useCallback, useEffect, useMemo, useState } from 'react';
import Product from '@/creditflowresearch/domain/Product';
import { useCorporateBondScreenerSidebarFilterContext } from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

type ICheckboxOption = { value: string, name: string, disabled: boolean };

const useCreditProductsSection = (sectionId: string) => {
  const {
    filters,
    localFilters,
    setLocalFilters,
    setRequiredFilterEmpty,
  } = useCorporateBondScreenerSidebarFilterContext();
  const { products: accessibleProductIds } = useUserPlanContext();
  const granted = useAccessGrant();

  const filterAccessible = useCallback(
    (ids: string[]): string[] => ids.filter((id) => granted || accessibleProductIds.includes(id)),
    [accessibleProductIds, granted],
  );

  const [selectedValues, setSelectedValues] = useState<string[]>(
    () => filterAccessible(localFilters.includedProductIds),
  );

  useEffect(() => {
    setLocalFilters((prev) => ({ ...prev, includedProductIds: filterAccessible(prev.includedProductIds) }));
  }, []);

  useEffect(() => {
    const enabledCount = filterAccessible(selectedValues).length;
    setRequiredFilterEmpty(sectionId, enabledCount === 0);
  }, [selectedValues, filterAccessible, setRequiredFilterEmpty, sectionId]);

  const items: ICheckboxOption[] = useMemo(() => (filters?.productIds ?? [])
    .map((id) => {
      const product = Product.from(id);
      return product ? {
        name: `${ product.getName() } (${ product.getAbbreviation() })`,
        value: id,
        disabled: !granted && !accessibleProductIds.includes(id),
      } : null;
    })
    .filter(Boolean), [filters?.productIds, accessibleProductIds, granted]);

  const allValues = useMemo(() => items.map((item) => item.value), [items]);

  const accessibleValues = useMemo(() => filterAccessible(allValues), [filterAccessible, allValues]);

  const onChange = useCallback((value: string) => {
    setSelectedValues((prev) => {
      const next = filterAccessible(prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
      setLocalFilters((prevFilters) => ({ ...prevFilters, includedProductIds: next }));
      return next;
    });
  }, [setLocalFilters, filterAccessible]);

  const onSelectAll = useCallback(() => {
    setSelectedValues((prev) => {
      const next = prev.length === accessibleValues.length ? [] : accessibleValues;
      setLocalFilters((prevFilters) => ({ ...prevFilters, includedProductIds: next }));
      return next;
    });
  }, [setLocalFilters, accessibleValues]);

  return {
    items,
    selectedValues,
    isAllSelected: selectedValues.length === accessibleValues.length,
    isIndeterminate: selectedValues.length > 0 && selectedValues.length < accessibleValues.length,
    onChange,
    onSelectAll,
  };
};

export default useCreditProductsSection;
